/* eslint-disable object-curly-newline, object-property-newline, no-nested-ternary */
//
// Hybrid COIN detector -- programmatic_v2 Spike B port.
//
// Combines:
//   (1) compositional clause-level patterns: regex over sentences/clauses
//       to detect Context/Observation/Impact/Next-Steps from structural
//       cues, not just keyword presence.
//   (2) the existing legacy keyword detection from scoring.js (passed in
//       as legacyCoin) so we keep its recall.
//
// Output: { context, observation, impact, next_steps } where each is bool.
// Hybrid rule: element present iff (legacy says yes) OR (comp says yes).
// EXCEPT: legacy "observation" is suppressed when the comment is very
// short (<8 words) AND no specific action verb / deliverable / behavioral
// phrase is present. This catches the legacy FP pattern of single generic
// verbs ("Sofia contributes greatly to the group") that Claude scores 0.
//
// Verified vs the Python prototype on the 17,599-comment derived-3D
// Claude corpus: hybrid COIN with legacy matrix lifts structure r from
// 0.666 to 0.672 (+0.006). The fitted matrix was a band-aid that
// regressed on the 17k -- this port keeps the legacy STRUCTURE_MATRIX.

const SPECIFIC_ACTION_VERBS = new Set([
  'submitted', 'presented', 'delivered',
  'organized', 'scheduled', 'coordinated',
  'facilitated', 'delegated',
  'designed', 'built', 'created', 'drafted',
  'wrote', 'coded', 'implemented', 'tested', 'debugged',
  'reviewed', 'edited', 'proofread',
  'researched', 'investigated', 'explored',
  'led', 'ran', 'managed',
  'explained', 'demonstrated', 'taught',
  'interviewed', 'surveyed',
  'filmed', 'recorded', 'photographed', 'illustrated',
  'compiled', 'documented',
  'moderated', 'mediated',
  'rehearsed', 'performed', 'choreographed',
  'completed',
]);

const DELIVERABLE_NOUNS = new Set([
  'report', 'reports', 'presentation', 'presentations',
  'slides', 'slide', 'document', 'documents', 'doc', 'docs',
  'code', 'circuit', 'circuits', 'diagram', 'diagrams',
  'prototype', 'prototypes', 'model', 'models',
  'draft', 'drafts', 'outline', 'outlines',
  'section', 'sections', 'chapter', 'chapters',
  'assignment', 'assignments', 'milestone', 'milestones',
  'module', 'modules', 'deliverable', 'deliverables',
  'sketch', 'sketches', 'wireframe', 'wireframes',
  'spreadsheet', 'spreadsheets', 'calculation', 'calculations',
  'analysis', 'test', 'tests', 'study',
  'video', 'videos', 'interview', 'interviews',
  'paper', 'papers', 'thesis', 'manuscript',
  'tutorial', 'demo', 'demonstration',
  'gantt', 'schedule', 'timeline', 'agenda',
]);

const GENERIC_VERBS = new Set([
  'contributes', 'contribute', 'contributed',
  'works', 'work', 'worked',
  'helps', 'help', 'helped',
  'does', 'do', 'did', 'done',
  'is', 'was', 'are', 'were', 'be', 'been', 'being',
  'has', 'have', 'had',
  'shows', 'show', 'showed',
  'gives', 'give', 'gave',
  'takes', 'take', 'took',
  'makes', 'make', 'made',
]);

const TIME_PLACE_OPENERS = new Set(['when', 'during', 'while', 'throughout', 'after', 'before', 'at']);
const TIME_NOUNS = new Set([
  'meeting', 'meetings', 'class', 'lab', 'labs',
  'presentation', 'presentations', 'workshop', 'workshops',
  'week', 'weeks', 'month', 'months', 'semester', 'term', 'quarter',
  'phase', 'phases', 'project', 'stage', 'milestone',
  'morning', 'afternoon', 'evening',
  'session', 'sessions',
]);

// -- Sentence / clause segmentation ----------------------------------

const SENT_SPLIT_RE = /(?<=[.!?])\s+(?=[A-Z])|(?<=[.!?])\s*\n+/m;
const CLAUSE_SPLIT_RE = /\s*[;,]\s+(?=\w)|\s+\b(?:but|and|so|because|while|when|though|although|yet|however|which|that)\b\s+/i;
const WORD_RE = /[A-Za-z']+/g;

function sentences(text) {
  if (!text) return [];
  const chunks = text.trim().split(SENT_SPLIT_RE);
  const out = chunks.filter((c) => c && c.trim()).map((c) => c.trim());
  if (out.length === 0 && text.trim()) out.push(text.trim());
  return out;
}

function clauses(sentence) {
  return sentence.split(CLAUSE_SPLIT_RE).filter((p) => p && p.trim()).map((p) => p.trim());
}

function tokens(text) {
  return (text || '').toLowerCase().match(WORD_RE) || [];
}

// -- Compositional pattern arrays (ported from coin_compositional.py) -

const OBSERVATION_PATTERNS = [
  /\b(did|does|done|doing) (?:a |an )?(great|good|excellent|amazing|outstanding|wonderful|fantastic|incredible|solid|nice|awesome|exceptional) (?:job|work)\b/i,
  /\b(took (the )?initiative|stepped up|came prepared|missed deadlines|met deadlines|met (the|our) deadlines?|kept track|stayed on top|stayed on task|took (the )?lead|took charge|pulled (his|her|their) weight|showed up|put in (the )?(time|effort|work|hours)|went above and beyond)\b/i,
  /\b(always|consistently|often|frequently|regularly|constantly|usually) (\w+ing)\b/i,
  /\b(always|consistently|often|frequently|regularly|constantly|usually) (\w+ed|\w+s) (the|our|all|every|on|in|with|at|her|his|their|your)\b/i,
  /\b(always|consistently|often) (asking|coming|coming up|looking for|checking|reaching out|stepping up|going above|bringing|providing|putting)\b/i,
  /\b(by|through) (\w+ing)\b/i,
  /\b(cares about|focused on|focuses on) (quality|the work|the project|our work|details|the team|completing|making sure|getting|each|every)\b/i,
  /\b(good|great|excellent|skilled|adept) at (\w+ing|the|making|finding|keeping|coding|writing|design)\b/i,
  /\b(communicated|communicates|explained|responded|presented|delivered|contributed|worked|engaged|participated|helped) (well|effectively|clearly|consistently|reliably|professionally|thoughtfully|actively|fully|on time|on point|in (the |our )?(group chat|chat|meetings|sessions|discussions|class))\b/i,
  /\b(contributed|helped|assisted|supported) (to|in|with) (\w+ing|the design|the report|the code|the slides|the deliverable|the construction|finding|making|getting|completing|finishing|preparing|figuring|sorting|organizing|writing)\b/i,
  /\b(who|that) (contributed|wrote|drafted|coded|presented|delivered|organized|led|managed|supported|helped|stepped|took|went|came|always|consistently|did|does)\b/i,
  /\b(handed|submitted|delivered|brought) (in|her|his|their|your|the) (\w+|information|slides|sections|parts|work)\b/i,
  /\b(is|was|were|are) (always|consistently|usually|very )?(very )?(responsive|engaged|prepared|proactive|attentive|available|punctual|organized|reliable|consistent|present|on top|on track|on time|in tune)\b/i,
  /\b(your|his|her|their) (work|writing|coding|presentation|sections?|contribution|delivery|communication|effort|attention|input|ideas) (was|is|has been|were) (great|good|excellent|strong|outstanding|impressive|consistent|reliable|valuable|clear|creative|original)\b/i,
  /\b(printing|printed|sketching|sketched|drafting|drafted|drawing|drew|figuring|figured) (out |up )?(the|our|a) (\w+)\b/i,
  /\b(your|his|her|their) (clear|excellent|consistent|valuable|insightful|creative|strong|reliable) (communication|leadership|work|contributions?|insights?|ideas|input|writing|coding|delivery|effort|focus|attention)\b/i,
  /\b(great|good|valuable|insightful|creative|innovative|original) (ideas|insights|contributions?|suggestions|input|feedback|points) (about|on|for|regarding)\b/i,
  /\b(was|were) an? (awesome|great|good|excellent|outstanding|amazing|wonderful|reliable|valuable) (team|group|class|project) (member|player|partner|collaborator) who\b/i,
  /\b(showed up|came|attended|joined|made it) to (every|all|each|the|our) (meeting|meetings|session|sessions|class|workshop|lab)\b/i,
  /\b(your|his|her|their) (willingness|ability|tendency|attention|dedication|commitment) to (\w+|take on|step up|help|support|listen|reach|work)\b/i,
  /\b(responded|replied|answered|finished|completed|delivered|submitted) (quickly|promptly|on time|early|first|immediately|right away)\b/i,
];

const IMPACT_PATTERNS = [
  /\b(which|that|this)\s+(helped|allowed|enabled|made|meant|let|kept|saved|brought|gave|resulted|caused|allowed us)\b/i,
  /\b(so|so that|which meant that)\s+(we|the team|the group|everyone|everybody|i|us)\b/i,
  /\b(the team|the group|the project|we|us|everyone|i) (benefited|benefit|appreciated|valued|gained|enjoyed|relied)\b/i,
  /\bmade (a|the|our|us|the team|the group|the project|the assignments?|the meetings?|the work|the discussions?|the construction) (.+?)(better|easier|possible|smoother|stronger|faster|more (efficient|effective|productive)|much (better|easier|more))\b/i,
  /\b(really|greatly|significantly|substantially|tremendously|highly|truly) (helped|improved|enhanced|benefited?|appreciated|impacted|saved)\b/i,
  /\bkept (us|the team|the group|everyone|things) (on track|focused|together|organized|going|aligned|moving|motivated|engaged|on time)\b/i,
  /\b(saved|saved us|gave us|gained us) (time|effort|hours|days|the project|the group|a lot|us)\b/i,
  /\b(asset|pleasure|key|crucial|essential|invaluable|huge help|big help|real help) to (the|our|my|all) (team|group|project|work|class)\b/i,
  /\b(helpful|useful|valuable|crucial|key|essential|critical|instrumental|vital) (to|in|for|with) (the team|the group|the project|us|me|finding|getting|making|completing|generating|keeping|figuring|sorting)\b/i,
  /\b(this|that|it) (allowed|let|enabled|helped|made it possible|gave) (us|the team|the group|me|everyone) (to|with)\b/i,
  /\b(built|built up|laid) (a|the) (foundation|base|groundwork|framework|standard)\b/i,
  /\b(is|was|were|are) (greatly|very|highly|deeply|truly|always) (valued|appreciated|cherished|relied (on|upon)|missed)\b/i,
  /\b(team|group|we|i|everyone|class) (often|usually|always|really) (benefits?|benefited|appreciates|appreciated|relies|relied) (from|on)\b/i,
  /\bgot (us|the team|the group|me|everyone) (through|to|past)\b/i,
  /\b(set the standard|raised the bar|set the tone|set a standard|set an example|set the pace)\b/i,
  /\bthanks (to you|for|to your|for your) (\w+|insights|input|help|leadership|contribution|effort|work|the)\b/i,
  /\b(would|wouldn't|would not|could not|couldn't) (have )?(been|got|made|finished|completed|achieved|gotten|been able) (\w+ ){0,3}(without|if not)\b/i,
  /\bmade .{1,40}(much )?more (efficient|effective|productive|smooth|clear|organized|focused)\b/i,
  /\b(pleasure|easy|great|nice|wonderful) (to work with|working with)\b/i,
];

const NEXT_STEPS_PATTERNS = [
  /\b(should|could|would|might|may) (try|work|focus|consider|continue|keep|improve|develop|practice|aim|push|engage|speak|share|reach|be|do|put|take|spend|set|build|review|prepare|attend|show|provide|offer|raise|seek|ask|start|move|step)\b/i,
  /\bwould (benefit|help|improve|be (good|great|helpful|nice|useful|valuable|beneficial))\b/i,
  /\b(consider|try) (\w+ing|to \w+)\b/i,
  /\b(next time|in the future|going forward|moving forward|for the next|for next|from now on|next semester|next project|next phase)\b/i,
  /\b(work on|focus on|build on) (\w+ing|your|the|specific|being|getting|developing)\b/i,
  /\b(keep|continue) (it up|going|doing|the|to|up the|on)\b/i,
  /\bI (suggest|recommend|encourage|advise|would like to see|would love to see)\b/i,
  /\b(could have|would have|should have|might have|may have) (been|benefited|done|made|provided|considered|tried|focused|put|been more)\b/i,
  /\b(i('d| would)? (encourage|advise|recommend|suggest|like|love) (you )?to|i('d| would)? like (you|to see))\b/i,
  /\b(speak|share|engage|participate|contribute|reach|step) (up |out )?more\b/i,
  /\b(be more|being more|become more) (vocal|engaged|present|involved|active|proactive|prepared|focused|consistent|organized)\b/i,
  /\b(asking|wondering|considering|thinking about) (how|what|where|whether|if) (we|the team|you|i) (can|should|could)\b/i,
  /\bhow (we|the team) (should|could|might|need to) (tackle|approach|handle|address|do)\b/i,
  /\b(perhaps|maybe|possibly) (try|consider|work|focus|aim|share|speak|engage|attend)\b/i,
];

// -- Per-element detectors (compositional) ---------------------------

function clauseHasContext(cl) {
  const toks = tokens(cl);
  if (toks.length === 0) return false;
  if (TIME_PLACE_OPENERS.has(toks[0]) && toks.length >= 4) return true;
  const windowMatch = toks.some((t, i) => {
    if (t === 'during' || t === 'at' || t === 'in' || t === 'on'
        || t === 'after' || t === 'before' || t === 'throughout') {
      return toks.slice(i + 1, i + 5).some((w) => TIME_NOUNS.has(w));
    }
    return false;
  });
  if (windowMatch) return true;
  if (/\b(part|phase|project|assignment|milestone|stage|module)\s*[1-9]\b/i.test(cl)) return true;
  if (/\b(first|last|past|recent|second|third|final|early|late)\s+(few|couple|several)?\s*(week|weeks|month|months|day|days|semester|term|quarter|phase|project)\b/i.test(cl)) return true;
  if (/\b(this|last|next|the)\s+(semester|term|year|week|month|quarter|project|phase)\b/i.test(cl)) return true;
  return false;
}

function detectContextComp(sentList) {
  return sentList.some((s) => clauses(s).some(clauseHasContext));
}

function clauseHasObservation(cl) {
  const toks = tokens(cl);
  if (toks.length < 3) return false;
  const tokArr = Array.from(new Set(toks));
  if (tokArr.some((t) => SPECIFIC_ACTION_VERBS.has(t))) return true;
  if (tokArr.some((t) => DELIVERABLE_NOUNS.has(t))
      && tokArr.some((t) => GENERIC_VERBS.has(t))) return true;
  return OBSERVATION_PATTERNS.some((p) => p.test(cl));
}

function detectObservationComp(sentList) {
  return sentList.some((s) => clauses(s).some(clauseHasObservation));
}

const CAUSAL_OPENERS = ['because of', 'thanks to', 'due to', 'as a result', 'as a result of', 'so that', 'in order to', 'in order that'];

function clauseHasImpact(cl) {
  const low = cl.toLowerCase();
  if (CAUSAL_OPENERS.some((c) => low.startsWith(c) || low.indexOf(c) !== -1)) return true;
  if (IMPACT_PATTERNS.some((p) => p.test(cl))) return true;
  if (/\b(huge|great|real|valuable|tremendous|incredible|major) (asset|help|contribution|difference|impact)\b/i.test(cl)) return true;
  return false;
}

function detectImpactComp(sentList) {
  return sentList.some((s) => clauses(s).some(clauseHasImpact));
}

const IMPERATIVE_OPENERS = new Set(['try', 'consider', 'think', 'focus', 'work', 'keep', 'continue', 'remember', 'make', 'be']);

function clauseHasNextSteps(cl) {
  const toks = tokens(cl);
  if (toks.length === 0) return false;
  if (NEXT_STEPS_PATTERNS.some((p) => p.test(cl))) return true;
  if (toks.length >= 2 && IMPERATIVE_OPENERS.has(toks[0])) return true;
  if (/\b(it would|it'd|it would be|it would help) (be )?(\w+) (to|if you)\b/i.test(cl)) return true;
  return false;
}

function detectNextStepsComp(sentList) {
  return sentList.some((s) => clauses(s).some(clauseHasNextSteps));
}

export function detectCoinCompositional(text) {
  const sentList = sentences(text);
  if (sentList.length === 0) {
    return { context: false, observation: false, impact: false, next_steps: false };
  }
  return {
    context: detectContextComp(sentList),
    observation: detectObservationComp(sentList),
    impact: detectImpactComp(sentList),
    next_steps: detectNextStepsComp(sentList),
  };
}

// -- Precision filter ------------------------------------------------

const LEGACY_OBS_PRECISION_PATTERNS = [
  /\b(did|does|done|doing) (?:a |an )?(great|good|excellent|amazing|outstanding|nice|awesome) (job|work)\b/i,
  /\b(was|were|is|are) (always|consistently|very) (responsive|engaged|prepared|punctual|reliable|organized|on time|on top|on track)\b/i,
  /\b(always|consistently|often|frequently|usually) (\w+ing|\w+ed)\b/i,
  /\b(took (the )?initiative|stepped up|came prepared|met deadlines|kept track|stayed on top|took (the )?lead|pulled (his|her|their) weight|put in (the )?(time|effort|work)|went above)\b/i,
  /\b(working|presenting|coding|writing|designing|building|drafting|reviewing|organizing|coordinating|leading|managing|researching) (the|our|each|all|on|with)\b/i,
];

function passesObservationPrecisionFilter(text) {
  const wc = (text || '').split(/\s+/).filter(Boolean).length;
  if (wc >= 8) return true;
  const lower = (text || '').toLowerCase();
  const tokArr = Array.from(new Set(lower.match(/[a-z']+/g) || []));
  if (tokArr.some((t) => SPECIFIC_ACTION_VERBS.has(t))) return true;
  if (tokArr.some((t) => DELIVERABLE_NOUNS.has(t))) return true;
  if (LEGACY_OBS_PRECISION_PATTERNS.some((p) => p.test(lower))) return true;
  return false;
}

// -- Hybrid combiner -------------------------------------------------

/**
 * Combine the legacy keyword COIN result with compositional patterns +
 * apply a precision filter on legacy observation FPs for very short
 * generic comments.
 *
 * @param {string} text - the comment text
 * @param {object} legacyCoin - existing scoring.js COIN result, shape:
 *   { context, observation, impact, next_steps } -- each bool
 * @returns {object} same shape, hybrid-combined.
 */
export function detectCoinHybrid(text, legacyCoin) {
  const comp = detectCoinCompositional(text);
  const union = {
    context: Boolean(legacyCoin && legacyCoin.context) || comp.context,
    observation: Boolean(legacyCoin && legacyCoin.observation) || comp.observation,
    impact: Boolean(legacyCoin && legacyCoin.impact) || comp.impact,
    next_steps: Boolean(legacyCoin && legacyCoin.next_steps) || comp.next_steps,
  };
  // Precision filter: if legacy fires observation but compositional does
  // not AND text doesn't pass the precision filter, suppress legacy's
  // observation hit. This catches "Sofia contributes greatly to the
  // group" style FPs.
  if (union.observation && !comp.observation) {
    if (!passesObservationPrecisionFilter(text)) {
      union.observation = false;
    }
  }
  return union;
}
